/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
// adopt
import { adopt } from 'react-adopt';
// graphQL
import gql from 'graphql-tag';
// global variables
import { defaultNumberOfRows as DEFAULT_NUMBER_OF_ROWS } from 'config';
// our components
import qs, { getQueryString } from 'helpers/queryStrings';
import RouterContext from 'helpers/routerContext';
import { Query, Mutation } from 'components/common/Query';
import { PageInfo } from 'components/common/Paginator';
import NotificationTypeList from './List';

/**
 * Query to fetch notification types from api
 */
export const NOTIFICATION_TYPES_QUERY = gql`
  query NotificationTypeList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $id: ID
    $name: String
    $category: ID
    $isActive: Boolean
  ) {
    adminNotificationTypes(
      first: $first
      after: $after
      last: $last
      before: $before
      id: $id
      name: $name
      category: $category
      isActive: $isActive
    ) @connection(key: "adminNotificationTypes") {
      pageInfo {
        ...PaginatorPageInfo
      }
      edges {
        node {
          id
          photo
          name
          description
          category {
            id
            name
          }
          enableSendPush
          enableSendEmail
          defaultUserSettingsEmail
          defaultUserSettingsPush
          isActive
          strategy
          example
        }
      }
    }
  }
  ${PageInfo}
`;

/**
 * Mutation to enable notification type
 */
export const ENABLE_NOTIFICATION_TYPE_MUTATION = gql`
  mutation($id: ID!) {
    enableNotificationType(input: { id: $id }) {
      notificationType {
        id
        isActive
        __typename
      }
      errors {
        field
        messages
        __typename
      }
      clientMutationId
      __typename
    }
  }
`;

/**
 * Mutation to disable notification type
 */
export const DISABLE_NOTIFICATION_TYPE_MUTATION = gql`
  mutation($id: ID!) {
    disableNotificationType(input: { id: $id }) {
      notificationType {
        id
        isActive
        __typename
      }
      errors {
        field
        messages
        __typename
      }
      clientMutationId
      __typename
    }
  }
`;

/**
 * Default filters in NotificationTypeList
 */
const defaultFilters = {
  id: undefined,
  name: undefined,
  category: undefined,
  isActive: undefined,
};

/**
 * @class
 * @extends Component
 */
class NotificationTypeListContainer extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    const { initialFilters } = props;
    const {
      numberOfRowsSelected,
      direction,
      cursor,
      ...initFilters
    } = initialFilters;

    this.state = {
      filters: {
        ...defaultFilters,
        ...initFilters,
      },
      numberOfRowsSelected:
        parseInt(numberOfRowsSelected, 0) || DEFAULT_NUMBER_OF_ROWS,
      pagination: {
        direction: direction || null,
        cursor: cursor || undefined,
      },
    };

    this.Compose = adopt({
      queryNotificationTypes: this.notificationTypes,
      mutateEnable: this.enableNotificationType,
      mutateDisable: this.disableNotificationType,
    });
  }

  /**
   * @description
   * Query callback to compose
   * @param {*} render - Query's render props.
   * @returns {*} Query component.
   */
  notificationTypes = ({ render }) => {
    const { filters, pagination, numberOfRowsSelected } = this.state;
    const paginationVariables =
      !pagination.direction || pagination.direction === 'NEXT'
        ? {
            first: numberOfRowsSelected,
            after: pagination.cursor,
          }
        : {
            last: numberOfRowsSelected,
            before: pagination.cursor,
          };
    return (
      <Query
        query={NOTIFICATION_TYPES_QUERY}
        variables={{
          ...filters,
          ...paginationVariables,
        }}
        notifyOnNetworkStatusChange
        fetchPolicy="cache-and-network"
      >
        {({ data = {}, loading, fetchMore }) => {
          this.fetchMore = fetchMore;
          return render({ data, loading, fetchMore });
        }}
      </Query>
    );
  };

  /**
   * @description
   * Mutation to enable notification type
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  enableNotificationType = ({ render }) => (
    <Mutation mutation={ENABLE_NOTIFICATION_TYPE_MUTATION}>
      {(mutate, result) => render({ mutate, result })}
    </Mutation>
  );

  /**
   * @description
   * Mutation to disable notification type
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  disableNotificationType = ({ render }) => (
    <Mutation mutation={DISABLE_NOTIFICATION_TYPE_MUTATION}>
      {(mutate, result) => render({ mutate, result })}
    </Mutation>
  );

  /**
   * @description
   * Filter rows according selected filters.
   *
   * @param {Object} filters - Filters to apply.
   */
  handleFilter = filters => {
    const history = this.context;
    this.setState({
      filters: {
        ...filters,
      },
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      ...filters,
      numberOfRowsSelected,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Change number of rows selected.
   *
   * @param {number} selectedItem - New number of rows selected.
   */
  handleChangeNumberOfRows = selectedItem => {
    const history = this.context;
    this.setState({
      numberOfRowsSelected: selectedItem,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      numberOfRowsSelected: selectedItem,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Fetch more rows when user change page.
   *
   * @param {Object} paginatorVariables - Pagination query's variables.
   */
  handleChangePage = ({ direction, cursor }) => {
    const history = this.context;
    this.setState({ pagination: { direction, cursor } });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      direction,
      cursor,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Refresh the list.
   * @returns {Function} - Fetch More function.
   */
  handleRefresh = () => {
    this.fetchMore({
      updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
    });
  };

  render() {
    const { filters, numberOfRowsSelected, pagination } = this.state;
    return (
      <this.Compose>
        {({
          queryNotificationTypes: { data, loading },
          mutateEnable,
          mutateDisable,
        }) => (
          <NotificationTypeList
            data={data}
            loading={Object.keys(data).length === 0}
            fullLoading={loading}
            numberOfRowsSelected={numberOfRowsSelected}
            paginationDirection={pagination.direction}
            changePage={paginationVariables => {
              this.handleChangePage(paginationVariables);
            }}
            changeNumberOfRows={this.handleChangeNumberOfRows}
            appliedFilter={filters}
            filter={this.handleFilter}
            refresh={this.handleRefresh}
            enableNotificationType={mutateEnable.mutate}
            disableNotificationType={mutateDisable.mutate}
          />
        )}
      </this.Compose>
    );
  }
}

/**
 * NotificationTypeListContainer PropTypes
 */
NotificationTypeListContainer.propTypes = {
  initialFilters: PropTypes.shape({}).isRequired,
};

export default NotificationTypeListContainer;
export { defaultFilters };
